import { useEffect, useRef } from 'react';
import { useChatStore } from '../store/chatStore';
import { useAuthStore } from '../store/authStore';
import { presenceToMembers } from '../store/presenceStore';
import chatService from '../services/chatService';
import {
  joinChatChannel,
  leaveChatChannel,
  getJoinedChatId,
  sendRead,
} from '../services/chatChannelService';
import {
  addChatNewMessageListener,
  addChatChannelAddedListener,
  addChatReadListener,
  addChatChannelUpdatedListener,
  addChatChannelRemovedListener,
  addChatMessageUpdatedListener,
  addChatMessageDeletedListener,
  addChatMessageReactedListener,
  addUserRejoinListener,
  addSessionEndedListener,
} from '../services/channelService';
import {
  loadCachedHistory,
  cacheHistoryPage,
  cacheMessage,
  forgetCachedMessage,
  forgetCachedChannel,
  sweepExpiredCache,
} from '../services/chatHistoryCache';
import { isServerPage } from '../utils/chatPageSpan';
import { messageAlerts } from '../utils/chatAlerts';
import { useChatUiStore } from '../store/chatUiStore';
import { playNewMessageSound } from '../utils/chatSound';
import { showChatNotification } from '../utils/chatNotification';
import { useUiModalStore } from '../store/uiModalStore';
import { usePreferencesStore } from '../store/preferencesStore';
import { channelDisplay } from '../components/chatHelpers';

const TYPING_TTL = 4000;
const NOTIF_BODY_MAX = 140;

/**
 * Build the OS-notification title/body for an incoming message. DMs read as
 * "Alice" / "<text>"; groups & channels put the conversation name in the title
 * and prefix the sender in the body ("# general" / "Alice: <text>").
 */
function buildChatNotification(channel, message, currentUserId) {
  const senderName = message?.sender?.name || 'Someone';
  const { name: convName, seed } = channelDisplay(channel, currentUserId);
  let text = (message?.body || '').trim();
  if (!text) {
    // Non-text message (image/attachment/secret) — the body isn't carried here.
    text =
      message?.kind === 'image'
        ? '📷 Photo'
        : message?.kind === 'secret'
          ? 'Sent an encrypted secret'
          : 'Sent an attachment';
  }
  if (text.length > NOTIF_BODY_MAX) text = `${text.slice(0, NOTIF_BODY_MAX - 1)}…`;

  // The toast icon is the same initials avatar the sidebar draws for this
  // conversation (the other person for a DM, the group/channel otherwise).
  const icon = { iconName: convName, iconSeed: seed };
  if (channel?.kind === 'dm' || !channel) {
    return { title: senderName, body: text, ...icon };
  }
  return { title: convName, body: `${senderName}: ${text}`, ...icon };
}

/**
 * Is the user *actually looking at* this conversation right now? Selecting a
 * channel leaves it joined in the background — activeChannelId stays set even
 * after navigating to another panel (Collections, etc.) — so "active" is not
 * the same as "being viewed". A message should only skip the unread badge and
 * the notification chime when all three hold: the window is focused, the chat
 * panel is the open view, and this is the selected channel.
 */
function isViewingChannel(channelId) {
  return (
    document.hasFocus() &&
    useUiModalStore.getState().activeTab === 'chat.panel' &&
    useChatStore.getState().activeChannelId === channelId
  );
}

// channelId -> highest read seq already sent to the server this session.
// A switch/focus/message burst used to fire the same cursor over BOTH
// transports repeatedly; this makes advancing the cursor idempotent and
// single-transport. Module-level (not a ref) so every call site shares it.
const sentReadSeqs = new Map();

// This module's own global effect below already clears sentReadSeqs when
// isAuthenticated/user?.id become falsy — but that guard is skipped when a
// session ends WITHOUT that transition being visible to this hook (e.g. a
// user-id change while isAuthenticated stays true throughout). Subscribing
// here directly closes that gap unconditionally: if account B is later a
// member of a chat channel with the same id account A had open on this
// device, a stale entry here would make advanceReadCursor treat A's
// last-sent seq as already sent and silently suppress B's first read-receipt
// push. Registered once, unconditionally, at module load.
addSessionEndedListener(() => {
  sentReadSeqs.clear();
});

// Test-only accessors so sessionLifecycleNoResidue.test.js can populate and
// observe this holder directly, without rendering the hook and driving a
// live read-receipt push through it. Not used by any production code path.
export function _setSentReadSeqForTests(channelId, seq) {
  sentReadSeqs.set(channelId, seq);
}
export function _hasSentReadSeqForTests(channelId) {
  return sentReadSeqs.has(channelId);
}

/**
 * Advance the server-side read cursor, once per position: skips values
 * already sent, prefers the live WS topic when this conversation is joined,
 * and falls back to REST otherwise. On (rare) failure the entry is rolled
 * back so a later call retries.
 */
function advanceReadCursor(channelId, seq) {
  if (!channelId || !seq) return;
  if ((sentReadSeqs.get(channelId) || 0) >= seq) return;
  sentReadSeqs.set(channelId, seq);
  // Either transport rolls the entry back on failure so the next
  // markViewed/markReadLatest retries — otherwise a push lost to a dropping
  // socket would leave the cursor stuck for the whole session.
  const rollback = () => {
    if ((sentReadSeqs.get(channelId) || 0) <= seq) sentReadSeqs.delete(channelId);
  };
  if (getJoinedChatId() === channelId) {
    const push = sendRead(channelId, seq);
    if (push) push.receive('error', rollback).receive('timeout', rollback);
    else rollback();
  } else {
    chatService.markRead(channelId, seq).catch(rollback);
  }
}

// Latest persisted seq in a channel's buffer (optimistic messages have none).
function lastSeqOf(msgs) {
  for (let i = msgs.length - 1; i >= 0; i -= 1) {
    if (msgs[i].server_seq) return msgs[i].server_seq;
  }
  return 0;
}

/**
 * Always-on chat realtime orchestrator (mounted once in App). Two jobs:
 *  1. Global: keep the channel list current and bump unread counts from the
 *     user:{id} channel, so the rail badge is live even when chat is closed.
 *     Read receipts, membership updates, and removals arrive here too.
 *  2. Active conversation: join the chat:{id} topic for the selected channel,
 *     stream messages/typing/presence into the store, load history, and keep
 *     the read cursor advanced. Only one topic is joined at a time.
 */
export function useChatRealtime() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const activeChannelId = useChatStore((s) => s.activeChannelId);
  const activeTab = useUiModalStore((s) => s.activeTab);
  const typingTimers = useRef({});
  const presenceState = useRef({});

  // ── Mark the selected conversation read when the user actually views it ──
  // The join effect below only fires when the *channel* changes, so switching
  // back to the chat panel (or refocusing the window) after reading elsewhere
  // wouldn't otherwise clear the badge the global handler raised.
  useEffect(() => {
    const markViewed = () => {
      const chId = useChatStore.getState().activeChannelId;
      if (!chId || !isViewingChannel(chId)) return;
      const s = useChatStore.getState();
      s.clearUnread(chId);
      advanceReadCursor(chId, lastSeqOf(s.messagesByChannel[chId] || []));
    };
    markViewed();
    window.addEventListener('focus', markViewed);
    return () => window.removeEventListener('focus', markViewed);
  }, [activeTab, activeChannelId]);

  // ── Global: load channels + subscribe to user-channel chat events ────────
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      // Clear all chat state on logout so it doesn't leak to the next session
      // — including unsent drafts, which are the previous user's words.
      useChatStore.getState().reset();
      useChatUiStore.setState({ draftsByChannel: {} });
      sentReadSeqs.clear();
      return undefined;
    }

    const store = useChatStore.getState();
    store.setCurrentUserId(user.id);

    // Expiry sweep for the on-disk history cache. Read-time purging already
    // guarantees an expired `secret` is never SERVED; this is what stops one's
    // ciphertext and wrapped keys from sitting on disk indefinitely in a
    // conversation the user never happens to reopen.
    sweepExpiredCache();

    chatService.listChannels().then(store.setChannels).catch(() => {});

    // The read-receipt privacy flag is server-enforced; the server copy wins
    // over whatever the local prefs file says (e.g. toggled on another device).
    chatService
      .getChatPreferences()
      .then(({ read_receipts }) => {
        const prefs = usePreferencesStore.getState();
        const local = prefs.readReceipts !== false;
        if (read_receipts !== local) prefs.updatePreference('readReceipts', read_receipts);
      })
      .catch(() => {});

    const offNew = addChatNewMessageListener(({ channel_id, message }) => {
      const s = useChatStore.getState();
      const known = s.channels.some((c) => c.id === channel_id);
      if (!known) {
        // A brand-new DM/channel we don't have yet — refetch the list.
        chatService.listChannels().then(s.setChannels).catch(() => {});
      }
      // Backend never echoes the sender, so every hit here is an incoming
      // message. Unless the user is actually looking at this conversation,
      // surface it: bump the rail badge, play the chime, and — when the app is
      // in the background — pop a desktop notification.
      if (!isViewingChannel(channel_id)) {
        const channel = s.channels.find((c) => c.id === channel_id) || null;
        // One place decides what an arrival is allowed to do — mute suppresses
        // the chime and the OS toast but never the unread count, and that split
        // lives in utils/chatAlerts.js so the menu's promise and this hook's
        // behaviour cannot drift apart.
        const alerts = messageAlerts({
          channel,
          prefs: usePreferencesStore.getState(),
          hasFocus: document.hasFocus(),
        });
        if (alerts.unread) s.incrementUnread(channel_id);
        if (alerts.sound) playNewMessageSound();
        if (alerts.toast) {
          const { title, body, iconName, iconSeed } = buildChatNotification(channel, message, s.currentUserId);
          showChatNotification({ title, body, channelId: channel_id, iconName, iconSeed });
        }
      }
      // Reorder (touch updated_at) and keep the channel tip fresh — unread
      // recomputations (own-device read sync below) divide against
      // channel.server_seq, which otherwise stays frozen at the last full
      // list fetch. Monotonic: a late-delivered event can't regress it.
      const prevCh = s.channels.find((c) => c.id === channel_id);
      s.upsertChannel({
        id: channel_id,
        updated_at: message?.inserted_at || new Date().toISOString(),
        server_seq: Math.max(prevCh?.server_seq || 0, message?.server_seq || 0),
      });
    });

    const offAdded = addChatChannelAddedListener(() => {
      const s = useChatStore.getState();
      chatService.listChannels().then(s.setChannels).catch(() => {});
    });

    // COMMS-07: user:<id> (this whole effect's transport) has no gap-fill —
    // a reconnect after any blip silently drops chat:new_message/read/etc.
    // events that fired while disconnected, for the rest of the session.
    // Refetching the channel list on rejoin at least catches up unread
    // counts/new-conversation discovery (the currently OPEN conversation's
    // own missed messages are separately gap-filled by chatChannelService's
    // onRejoin -> fillGapIfStale).
    const offRejoined = addUserRejoinListener(() => {
      const s = useChatStore.getState();
      chatService.listChannels().then(s.setChannels).catch(() => {});
    });

    // Someone's read cursor advanced. Feeds the sender-side checkmarks; when
    // it's our own cursor (another device of ours read), it also settles the
    // unread badge so devices agree.
    const offRead = addChatReadListener(({ channel_id, user_id, seq }) => {
      const s = useChatStore.getState();
      s.updateMemberReadSeq(channel_id, user_id, seq);
      if (user_id === s.currentUserId) {
        // Best local knowledge of the channel tip — the channel row's
        // server_seq or the buffer tail, whichever is newer. Skip entirely
        // when neither is known rather than zeroing a real unread count.
        const ch = s.channels.find((c) => c.id === channel_id);
        const tip = Math.max(ch?.server_seq || 0, lastSeqOf(s.messagesByChannel[channel_id] || []));
        if (tip > 0) s.setUnread(channel_id, Math.max(0, tip - seq));
        if ((sentReadSeqs.get(channel_id) || 0) < seq) sentReadSeqs.set(channel_id, seq);
      }
    });

    // Membership/metadata changed (member added/removed, rename, avatar) —
    // the payload is the fresh channel serialized for this viewer.
    const offUpdated = addChatChannelUpdatedListener(({ channel }) => {
      if (channel?.id) useChatStore.getState().upsertChannel(channel);
    });

    const offRemoved = addChatChannelRemovedListener(({ channel_id }) => {
      if (channel_id) {
        useChatStore.getState().removeChannel(channel_id);
        // removeChannel drops the in-memory buffer; the on-disk copy has to go
        // too, or leaving a conversation would leave its history rendering
        // from cache on the next launch. Idempotent — `ChannelMembersPopover`'s
        // self-leave already does the same thing without waiting for this
        // broadcast, which may never arrive if the socket is down.
        forgetCachedChannel(channel_id);
      }
    });

    // Edits/unsends/reactions for conversations that are NOT the joined one —
    // keeps background cached buffers honest (an unsent message must vanish
    // everywhere, not just for whoever has the conversation open).
    const offMsgUpdated = addChatMessageUpdatedListener(({ channel_id, message }) => {
      if (channel_id && message) {
        useChatStore.getState().updateMessage(channel_id, message);
        // Keep the on-disk copy honest too: a cached pre-edit body that
        // survives a restart shows the user something nobody said.
        cacheMessage(channel_id, message);
      }
    });

    const offMsgDeleted = addChatMessageDeletedListener(({ channel_id, id }) => {
      if (channel_id && id != null) {
        useChatStore.getState().removeMessage(channel_id, id);
        // An unsend must not survive a restart. This is the live half only —
        // a device that was CLOSED when the unsend happened never sees this
        // event, and is covered by cacheHistoryPage's reconciliation instead.
        forgetCachedMessage(channel_id, id);
      }
    });

    const offMsgReacted = addChatMessageReactedListener(({ channel_id, id, reactions }) => {
      if (channel_id && id != null) {
        useChatStore.getState().updateMessage(channel_id, { id, reactions });
      }
    });

    return () => {
      offNew();
      offAdded();
      offRead();
      offUpdated();
      offRemoved();
      offMsgUpdated();
      offMsgDeleted();
      offMsgReacted();
      offRejoined();
    };
  }, [isAuthenticated, user?.id]);

  // ── Active conversation: join topic, stream, load history ────────────────
  useEffect(() => {
    if (!activeChannelId) {
      leaveChatChannel();
      return undefined;
    }

    presenceState.current = {};
    const store = useChatStore.getState();
    store.clearUnread(activeChannelId);

    const markReadLatest = () => {
      const msgs = useChatStore.getState().messagesByChannel[activeChannelId] || [];
      advanceReadCursor(activeChannelId, lastSeqOf(msgs));
    };

    // History strategy: a cached buffer renders instantly, so only fetch when
    // it might be stale — the join reply carries the channel's server_seq and
    // a matching cached tail means there is nothing to fetch at all. A cold
    // channel fetches immediately (no reason to wait a WS round-trip) behind a
    // loading flag. Fetched pages MERGE into the buffer; replacing it would
    // throw away paged-in scrollback and yank the reader's position.
    const cached = store.messagesByChannel[activeChannelId] || [];
    if (cached.length === 0) {
      store.setHistoryLoading(activeChannelId, true);

      // Taken BEFORE the disk read is even dispatched, because the disk read is
      // where the tip below comes from and `cacheMessage` writes live arrivals
      // into the same table it has not executed against yet. Everything that
      // arrives from here on is a message the server's page could not have
      // known about, whatever seq the read eventually reports. See
      // `utils/chatPageSpan.js`'s `exemptUids` section.
      const arrivalMark = store.liveArrivalMark(activeChannelId);

      // Paint from the on-disk cache first (services/chatHistoryCache.js). On
      // a fresh launch the in-memory buffer is always empty, so without this
      // every first open of every conversation waits a REST round-trip before
      // showing anything.
      //
      // This does NOT replace the fetch below, and must not be made to: a
      // server-side unsend does not advance the channel's server_seq, so a
      // cached tail matching the channel tip is NOT evidence that nothing
      // changed. Painting and then verifying is what keeps an unsent message
      // from rendering forever. The cache removes the WAIT, not the request.
      //
      // And it deliberately does NOT mark the channel verified — see
      // `chatStore.historyVerified`. If the fetch below fails, this buffer must
      // still read as unverified so the WS join's gap-fill retries.
      const diskRead = loadCachedHistory(activeChannelId).then((disk) => {
        const st = useChatStore.getState();
        // Late-resolving, exactly like the fetch: don't write into a
        // conversation the user has since left or been removed from, and don't
        // clobber a fetch that already landed. That second guard is
        // `historyVerified`, NOT "is the buffer empty" — an empty tip page
        // EMPTIES the buffer, and a length check reads the result of that purge
        // as "nothing has landed yet" and repaints the history the page just
        // proved was gone.
        if (!disk.length || !stillRelevant(st)) return disk;
        if (st.historyVerified[activeChannelId]) return disk;
        st.mergeMessages(activeChannelId, disk);
        return disk;
      });

      chatService
        .getMessages(activeChannelId, { limit: 50 })
        .then(async (msgs) => {
          // A page's absences may only delete up to the tip THIS DEVICE knew
          // about when the request went out (utils/chatPageSpan.js). On a cold
          // open the buffer is empty by definition, so the disk cache is that
          // knowledge — and it is AWAITED here rather than read from whichever
          // promise happened to win, so the bound (and the paint-then-reconcile
          // ordering) cannot depend on a race. The disk read was issued first
          // and never rejects; `loadCachedHistory` turns every failure into an
          // empty page.
          //
          // That bound is necessary and NOT sufficient: this read resolves
          // AFTER the request went out, and `cacheMessage` may have written a
          // live arrival into the same table meanwhile — in which case the tip
          // it reports is above a message the page could not speak for.
          // `exemptUids` closes that window by naming those arrivals outright.
          //
          // Known cost, deliberately not defended against: this makes the
          // NETWORK paint hostage to the LOCAL read. `loadCachedHistory` never
          // rejects, but it can also never resolve — a `catch` is not a timeout.
          // A hung `invoke` therefore leaves `historyLoading` true forever, and
          // the WS join cannot rescue it (`fillGapIfStale` returns early while
          // `historyLoading` is set), so the conversation shows a spinner for
          // the rest of the session. There is no timeout here on purpose:
          // `chat_cache_load` is a NON-async `#[tauri::command]`, so the failure
          // mode that would actually produce a hang blocks Tauri's command
          // thread and freezes the app rather than leaving a live webview
          // spinning. A JS-side race would hide that symptom without changing
          // the outcome, which is worse than showing it. If `chat_cache_load`
          // ever becomes `async` (or starts doing unbounded work), revisit this
          // — at that point a timeout resolving to `[]` is both cheap and
          // conservative, since an unknown tip already means "delete nothing".
          const tipAtRequest = lastSeqOf(await diskRead);
          const st = useChatStore.getState();
          if (!stillRelevant(st)) return;
          const exemptUids = st.liveArrivalsSince(activeChannelId, arrivalMark);
          // A resolved promise is not necessarily an answer: `getMessages`
          // returns `res.data.messages`, so a 200 without that key resolves to
          // `undefined`. That is a failed fetch — it must delete nothing, cache
          // nothing and verify nothing — not an empty channel.
          if (!isServerPage(msgs)) return;
          st.noteHistoryWindow(activeChannelId, msgs.historyDays);
          // A page shorter than the limit is the whole of what the server has
          // for this viewer — there is nothing above it to scroll to.
          st.noteHistoryEnd(activeChannelId, msgs.length < 50);
          // Reconcile, not merge. A merge is a union, so a message unsent while
          // this device was closed would survive in the buffer for the rest of
          // the session even though the page (and the disk cache) no longer has
          // it. The page reaches the tip — no before_seq — so its absences are
          // authoritative from its oldest row up to the known tip.
          st.reconcileMessages(activeChannelId, msgs, { reachesTip: true, tipAtRequest, exemptUids });
          // Same reconciliation on disk, with the same bound AND the same
          // exemptions — `cacheMessage` has already written those arrivals to
          // disk, so the disk is in exactly the same position as the buffer.
          // Upserts carry edits; rows the page omits within the range it speaks
          // for were unsent server-side.
          cacheHistoryPage(activeChannelId, msgs, { reachesTip: true, tipAtRequest, exemptUids });
          // The buffer has now been confirmed against the server.
          st.markHistoryVerified(activeChannelId);
          markReadLatest();
        })
        .catch(() => {})
        .finally(() => useChatStore.getState().setHistoryLoading(activeChannelId, false));
    }

    // A late-resolving fetch must not write into a conversation the user was
    // meanwhile removed from — merging would resurrect the buffer that
    // removeChannel just cleared. (While the channel list itself is still
    // loading, the active selection vouches for the channel instead.)
    const stillRelevant = (s) =>
      s.activeChannelId === activeChannelId || s.channels.some((c) => c.id === activeChannelId);

    const fillGapIfStale = (serverSeq) => {
      const s = useChatStore.getState();
      const buf = s.messagesByChannel[activeChannelId] || [];
      if (buf.length === 0) {
        // Empty buffer: skip only while the cold-open fetch owns the load or
        // the channel is genuinely empty server-side. Otherwise — reconnect
        // after an outage, or the initial fetch failed — nothing else will
        // ever load these messages, so fetch here.
        if (s.historyLoading[activeChannelId] || !serverSeq) return;
      } else if (!s.historyVerified[activeChannelId]) {
        // Non-empty, but nothing has confirmed it against the server — it was
        // painted from the on-disk cache and the cold-open fetch has not (yet)
        // succeeded. `lastSeqOf(buf) >= serverSeq` would be TRUE here, because
        // an unsend does not advance server_seq, so the tip test below would
        // return early and this session would never verify anything. Fall
        // through and fetch — unless the cold-open fetch is still in flight, in
        // which case it owns the load and a second request is pure duplication.
        if (s.historyLoading[activeChannelId]) return;
      } else if (serverSeq != null && lastSeqOf(buf) >= serverSeq) {
        markReadLatest();
        return;
      }
      // The tip this device knows about right now, captured BEFORE the request
      // goes out: on this path the buffer is the device's knowledge (it was
      // either painted from disk or fetched), and anything above this seq that
      // the page omits arrived while the page was in flight.
      const tipAtRequest = lastSeqOf(buf);
      // The buffer is read synchronously here, so the bound is already honest on
      // this path — but the exemption is carried anyway so ONE rule applies to
      // every tip page in the app rather than two rules that must be kept in
      // step. It is a no-op whenever nothing arrives mid-flight.
      const arrivalMark = s.liveArrivalMark(activeChannelId);
      chatService
        .getMessages(activeChannelId, { limit: 50 })
        .then((msgs) => {
          const st = useChatStore.getState();
          if (!stillRelevant(st)) return;
          const exemptUids = st.liveArrivalsSince(activeChannelId, arrivalMark);
          // See the cold-open fetch: a 200 with no `messages` key resolves to
          // `undefined`, which is a failed fetch and not an empty channel.
          if (!isServerPage(msgs)) return;
          st.noteHistoryWindow(activeChannelId, msgs.historyDays);
          // A page shorter than the limit is the whole of what the server has
          // for this viewer — there is nothing above it to scroll to.
          st.noteHistoryEnd(activeChannelId, msgs.length < 50);
          const cur = st.messagesByChannel[activeChannelId] || [];
          const oldestFetched = msgs.find((m) => m.server_seq)?.server_seq || 0;
          // If the missed stretch exceeds the page, merging would leave a
          // silent hole between the cached tail and the fetched page that
          // upward paging (keyed off the buffer's oldest seq) can never fill —
          // only then is dropping the old buffer the lesser evil. Unsent
          // (seq-less optimistic/failed) messages are the user's own typed
          // words and are carried over regardless.
          // A plan-windowed page starts at the window, not at the next unseen
          // message, so the "gap" it appears to leave is the hidden history
          // itself. Dropping the channel there deletes the device's only copy
          // of messages the server will never serve this account again —
          // the same trap `pageDeletionSpan` and `cacheHistoryPage` avoid.
          const windowed = Number.isFinite(msgs.historyDays);
          if (!windowed && cur.length && oldestFetched > lastSeqOf(cur) + 1) {
            const unsent = cur.filter((m) => !m.server_seq);
            st.setMessages(activeChannelId, [...msgs, ...unsent]);
            // The disk cache needs the same treatment as the buffer: keeping
            // the pre-gap rows would leave a hole between them and this page
            // that a cold start would render as one continuous conversation.
            // Drop the channel, then seed it with the page we just trusted.
            forgetCachedChannel(activeChannelId).then(() =>
              cacheHistoryPage(activeChannelId, msgs, { reachesTip: true, tipAtRequest, exemptUids })
            );
          } else {
            st.reconcileMessages(activeChannelId, msgs, { reachesTip: true, tipAtRequest, exemptUids });
            cacheHistoryPage(activeChannelId, msgs, { reachesTip: true, tipAtRequest, exemptUids });
          }
          st.markHistoryVerified(activeChannelId);
          markReadLatest();
        })
        .catch(() => {});
    };

    joinChatChannel(activeChannelId, {
      onJoined: (resp) => {
        const s = useChatStore.getState();
        if (resp?.channel_key) s.setChannelKey(activeChannelId, resp.channel_key);
        // Fresh per-member read cursors (privacy-scrubbed server-side) —
        // receipts that advanced while this conversation wasn't open.
        if (resp?.read_seqs) s.applyReadSeqs(activeChannelId, resp.read_seqs);
        fillGapIfStale(resp?.server_seq);
      },
      onMessage: (msg) => {
        const s = useChatStore.getState();
        s.addMessage(activeChannelId, msg);
        // Persist the confirmed arrival so the next launch already has it.
        cacheMessage(activeChannelId, msg);
        // Sender's own typing indicator (if any) is now moot.
        if (msg.sender_id) s.clearTyping(activeChannelId, msg.sender_id);
        // Only auto-mark-read when the user is actually viewing this channel.
        // The topic stays joined in the background after you navigate away, so
        // without this guard a message read here would silently swallow the
        // unread badge the global handler just raised.
        if (isViewingChannel(activeChannelId)) {
          s.clearUnread(activeChannelId);
          advanceReadCursor(activeChannelId, msg.server_seq);
        }
      },
      onMessageUpdated: (msg) => {
        useChatStore.getState().updateMessage(activeChannelId, msg);
        cacheMessage(activeChannelId, msg);
      },
      onMessageDeleted: ({ id }) => {
        useChatStore.getState().removeMessage(activeChannelId, id);
        forgetCachedMessage(activeChannelId, id);
      },
      onMessageReacted: (payload) => {
        useChatStore.getState().updateMessage(activeChannelId, payload);
      },
      onTyping: ({ user_id, name }) => {
        if (user_id === user?.id) return;
        const s = useChatStore.getState();
        s.setTyping(activeChannelId, user_id, name);
        clearTimeout(typingTimers.current[user_id]);
        typingTimers.current[user_id] = setTimeout(
          () => useChatStore.getState().clearTyping(activeChannelId, user_id),
          TYPING_TTL
        );
      },
      onPresenceState: (state) => {
        presenceState.current = state || {};
        useChatStore.getState().setPresence(activeChannelId, presenceToMembers(presenceState.current));
      },
      onPresenceDiff: ({ joins, leaves }) => {
        const next = { ...presenceState.current };
        Object.keys(leaves || {}).forEach((k) => delete next[k]);
        Object.entries(joins || {}).forEach(([k, v]) => { next[k] = v; });
        presenceState.current = next;
        useChatStore.getState().setPresence(activeChannelId, presenceToMembers(next));
      },
      // Broadcasts during a disconnect were not replayed; onJoined already ran
      // fillGapIfStale with the rejoin reply's server_seq, so nothing extra to
      // do here — kept as an explicit no-op for readability.
      onRejoin: () => {},
    });

    const timers = typingTimers.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
      leaveChatChannel();
      // Deep scrollback paged in during this visit is released on leave.
      useChatStore.getState().trimChannel(activeChannelId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannelId, user?.id]);
}
